import React, { useState, useEffect, useRef } from "react";
import "./ListBox.scss";

const ListBox = ({
  data = [],
  placeholder = "Search",
  onSelect = () => {},
  onAction = () => {},
  actions = [],
  selectedId = null,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const items = data || [];

  const itemRefs = useRef({});

  useEffect(() => {
    if (selectedId && items.length) {
      const found = items.find((item) => item.id === selectedId);

      if (found) {
        setSelectedItem(found);

        const node = itemRefs.current[selectedId];
        const listRoot = node?.closest(".listbox__list");
        if (node && listRoot) {
          const rootRect = listRoot.getBoundingClientRect();
          const nodeRect = node.getBoundingClientRect();
          const isVisible =
            nodeRect.top >= rootRect.top && nodeRect.bottom <= rootRect.bottom;
          if (!isVisible) {
            node.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
            });
          }
        }
      }
    }
  }, [selectedId, items]);

  const filtered = searchTerm.trim()
    ? items.filter((item) =>
        JSON.stringify(item).toLowerCase().includes(searchTerm.toLowerCase())
      )
    : items;

  const handleSelect = (item) => {
    setSelectedItem(item);
    onSelect(item);
  };

  const handleActionClick = (e, action, item) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedItem(item);
    onAction({ action, item });
  };

  return (
    <div className="listbox__list-container">
      <div className="listbox__search">
        <i className="fas fa-search"></i>
        <input
          type="text"
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm ? (
          <button
            type="button"
            className="listbox__search-clear"
            aria-label="Clear search"
            onClick={() => setSearchTerm("")}
          >
            <i className="fas fa-times"></i>
          </button>
        ) : null}
      </div>

      <div className="listbox__list">
        <ul>
          {filtered.map((item) => (
            <li
              key={item.id}
              ref={(el) => (itemRefs.current[item.id] = el)}
              className={`listbox__item ${
                selectedItem?.id === item.id
                  ? "listbox__item--selected"
                  : ""
              }`}
              onClick={() => handleSelect(item)}
            >
              {/* User Avatar */}
              {item.dataType === "User" && (
                <span className="listbox__avatar">
                  {item.profilePic ? (
                    <img src={item.profilePic} alt={item.title} />
                  ) : (
                    <span className="listbox__avatar--text">
                      {item.title?.charAt(0).toUpperCase()}
                    </span>
                  )}
                </span>
              )}

              {/* Study Dot */}
              {item.dataType === "Study" && (
                <span
                  className="listbox__study-dot"
                  style={{ backgroundColor: item.studyColor }}
                />
              )}

              {/* Text */}
              <span className="listbox__text">
                <span className="listbox__title">{item.title}</span>
                {item.subTitle && (
                  <span className="listbox__subtitle">
                    {item.subTitle}
                  </span>
                )}
              </span>

              {/* Actions */}
              {actions.length > 0 && (
                <span className="listbox__actions">
                  {actions.map((action) => (
                    <button
                      type="button"
                      key={action.key}
                      className={`listbox__action-btn${
                        action.danger ? " listbox__action-btn--danger" : ""
                      }`}
                      aria-label={action.label || action.key}
                      onClick={(e) =>
                        handleActionClick(e, action.key, item)
                      }
                    >
                      <i className={action.icon}></i>
                    </button>
                  ))}
                </span>
              )}
            </li>
          ))}
          {filtered.length === 0 && searchTerm.trim() ? (
            <li className="listbox__empty">No matches found</li>
          ) : null}
        </ul>
      </div>
    </div>
  );
};

export default ListBox;
